import { useEffect, useState } from 'react';
import { useUpdateEffect, useWindowSize } from 'react-use';
import { minBreakpoints } from '@noths/polaris-client-ribbons-base';

export const DEFAULT_LINE_HEIGHT = 24; // Default line height in pixels
export const SMALL_SCREEN_LINE_HEIGHT = 21; // Line height for screens < 1024px
export const SMALL_SCREEN_WIDTH = minBreakpoints.L; // Screen width threshold

interface UseExpandableTextProps {
  maxLines: number;
  ref: React.RefObject<HTMLElement>;
}

export const useExpandableText = ({ maxLines, ref }: UseExpandableTextProps) => {
  const [lineCount, setLineCount] = useState(0);
  const [height, setHeight] = useState(0);
  const [expandedHeight, setExpandedHeight] = useState(0);
  const { width } = useWindowSize(); // Track window size

  const lineHeight = width < SMALL_SCREEN_WIDTH ? SMALL_SCREEN_LINE_HEIGHT : DEFAULT_LINE_HEIGHT;

  const calculateValues = () => {
    if (ref.current) {
      const totalHeight = ref.current.scrollHeight;
      const calculatedLineCount = Math.ceil(totalHeight / lineHeight);
      setLineCount(calculatedLineCount);
      setHeight(Math.min(calculatedLineCount, maxLines) * lineHeight);
      setExpandedHeight(calculatedLineCount * lineHeight);
    }
  };

  // Recalculate values whenever ref, lineHeight or width changes
  useUpdateEffect(() => {
    calculateValues();
  }, [ref, lineHeight, width]);

  // Ensure values are calculated on the initial load
  useEffect(() => {
    calculateValues();
  }, []); // Run only once after the component mounts

  return { lineCount, height, expandedHeight };
};
